/* global t, runtime, mergeState, safeArray, num, isoToNb, buildLedgerLines, hasPrimaryAttachment, isActiveExpense, dashboardExpenseVatBreakdown, FIXED_ASSET_GROUP_CONFIGS */
// EIENDELER — delene av lesemodellen for byråets eiendelsskjerm
//
// Registeret, anskaffelsene, avskrivningene, avgangene og postene som krever en
// vurdering. Sammenstillingen, kontrollkontoene og kontrollene ligger i
// backend-assets-recon-view.jsx.
//
// Modellen er READ-ONLY og regner ikke ut et eneste avskrivningsbeløp selv:
// anleggsregisteret er `buildFixedAssetsModel`, det samme registeret
// eiendelssiden og årsoppgjøret leser. To steder å regne ut den samme
// avskrivningen ville gitt to svar, og avstemmingen ville avstemt mot seg selv.

const assetsReconRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

const assetsReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: year + '-' + pad(monthIndex + 1) + '-01',
    to: year + '-' + pad(monthIndex + 1) + '-' + pad(lastDay),
    yearFrom: year + '-01-01',
  };
};

// Beløpsgrensen er en SKATTEREGEL, ikke en innstilling byrået setter. Designets
// «virksomhetens aktiveringsgrense» finnes ikke i appen, og en oppfunnet
// innstilling ville gitt en terskel uten kilde. Regelen er den samme som
// functions/accounting/fixedAssetSignificance.ts håndhever
// (NO.TAX.FIXED_ASSET.SIGNIFICANCE.V1) — `assetsReconThreshold.test` i
// avstemmingEiendeler.test.js vokter at de to ikke driver fra hverandre.
//
// Grensen gjelder ETT driftsmiddel, ikke fakturasummen, og den gjelder bare
// Norge. For SE/DK finnes ingen tilsvarende regel i appen, og da er terskelen
// `null` — ikke 30 000 brukt på et land regelen ikke gjelder i.
const ASSETS_RECON_SIGNIFICANCE = window.PlummeFixedAssetSignificance.rule;
const assetsReconThreshold = ({ country, date } = {}) =>
  window.PlummeFixedAssetSignificance.thresholdForDate({ country, date });

// Kontoene anleggsregisteret selv fører på. De kommer fra saldogruppene i
// `FIXED_ASSET_GROUP_CONFIGS`, ikke fra en egen liste her: legges en gruppe til,
// følger avstemmingen med uten at noen må huske å oppdatere to steder.
const assetsReconGroupAccounts = () => {
  const asset = [];
  const accumulated = [];
  const depreciation = [];
  const byGroup = [];
  for (const group of safeArray(typeof FIXED_ASSET_GROUP_CONFIGS !== 'undefined' ? FIXED_ASSET_GROUP_CONFIGS : [])) {
    if (group.assetAccountCode) asset.push(String(group.assetAccountCode));
    if (group.accumulatedDepreciationAccountCode) accumulated.push(String(group.accumulatedDepreciationAccountCode));
    if (group.depreciationAccountCode) depreciation.push(String(group.depreciationAccountCode));
    byGroup.push({
      id: group.id,
      assetAccountCode: String(group.assetAccountCode || ''),
      accumulatedAccountCode: String(group.accumulatedDepreciationAccountCode || ''),
      depreciationAccountCode: String(group.depreciationAccountCode || ''),
    });
  }
  return { asset, accumulated, depreciation, byGroup };
};

// ------------------------------------------------------------------
// Anleggsregisteret
// ------------------------------------------------------------------
// Radene er registerets egne. Varslene er det avstemmingen legger til: hvert av
// dem peker på noe dataene faktisk sier, ingen av dem er en konklusjon.
const assetsReconRegister = ({ model, range }) => {
  const rows = safeArray(model?.assets).filter((asset) => asset.status !== 'deleted');
  return rows.map((asset) => {
    const acquired = String(asset.isoDate || '').slice(0, 10);
    const sold = String(asset.entry?.fixedAsset?.soldAt || '').slice(0, 10);
    const bookValue = num(asset.ut);
    const yearRow = safeArray(asset.depreciationRows).find((row) => Number(row.ar) === range.year) || null;
    const flags = [];
    if (acquired && acquired >= range.yearFrom && acquired <= range.to) flags.push('ny');
    if (sold && sold >= range.from && sold <= range.to) flags.push('avgang');
    // Fullt avskrevet OG fortsatt i registeret er en levetidsvurdering, ikke en
    // feil: enten brukes eiendelen fortsatt, eller så skal den utrangeres.
    if (asset.status === 'written-off') flags.push('fullt_avskrevet');
    // Et kjøp uten bilag er ikke dokumentert anskaffelseskost. Importerte
    // eiendeler har aldri et bilag i appen, og flagges derfor ikke.
    if (!asset.importedArchiveOnly && !hasPrimaryAttachment(asset.entry)) flags.push('mangler_dok');
    if (asset.bookPlan && !asset.bookPlan.valid) flags.push('mangler_bokplan');
    if (asset.status === 'active' && bookValue > 0.005 && !asset.impairmentAssessment) flags.push('mangler_verdifallsvurdering');
    if (asset.status === 'active' && bookValue > 0.005 && !yearRow) flags.push('uten_avskrivning');
    if (bookValue < -0.005) flags.push('negativ_verdi');
    return {
      id: asset.id,
      name: asset.name,
      group: asset.group,
      groupLabel: asset.groupLabel,
      rate: num(asset.rate),
      usefulLifeMonths: num(asset.usefulLifeMonths),
      residualValue: assetsReconRound(asset.residualValue),
      accountCode: String(asset.acct || ''),
      acquiredDate: acquired,
      acquiredLabel: asset.date || (acquired ? isoToNb(acquired) : '—'),
      cost: assetsReconRound(asset.cost),
      accumulated: assetsReconRound(asset.dep),
      bookValue: assetsReconRound(bookValue),
      yearDepreciation: yearRow ? assetsReconRound(Math.abs(num(yearRow.endring))) : 0,
      status: asset.status,
      soldDate: sold,
      saleAmount: assetsReconRound(asset.saleAmountNet),
      // Bokfoert verdi ved INNGANGEN til avgangsaaret. Registerets `ut` duger
      // ikke: saldoberegningen trekker salgsvederlaget fra grunnlaget i
      // salgsaaret, saa `ut` er verdien ETTER avgangen. Gevinsten maa maales
      // mot verdien eiendelen hadde da den ble solgt.
      bookValueBeforeDisposal: sold
        ? assetsReconRound(safeArray(asset.depreciationRows)
          .filter((row) => Number(row.ar) < Number(String(sold).slice(0, 4)))
          .reduce((value, row) => num(row.nyVerdi), num(asset.cost)))
        : null,
      imported: Boolean(asset.importedArchiveOnly),
      documented: !asset.importedArchiveOnly && hasPrimaryAttachment(asset.entry),
      expenseId: asset.entry?.id || asset.id,
      flags,
    };
  });
};

// ------------------------------------------------------------------
// Anskaffelser og aktiveringskontroll
// ------------------------------------------------------------------
// Kjøpene i perioden, med den behandlingen de FAKTISK har fått. Terskelen er et
// kontrollsignal og ikke en konklusjon: Plumme avgjør ikke at et kjøp skal
// aktiveres. Varselet sier bare at kjøpet er stort nok til at spørsmålet må
// stilles, og et kjøp under grensen kan fortsatt være aktiveringspliktig hvis
// levetiden er lang nok.
const assetsReconAcquisitions = ({ state, range, threshold, assetAccounts }) => {
  const assetAccountSet = new Set(assetAccounts);
  return safeArray(state.expenses)
    .filter((expense) => isActiveExpense(expense))
    .filter((expense) => {
      const date = String(expense.date || '').slice(0, 10);
      return date >= range.from && date <= range.to;
    })
    .map((expense) => {
      const breakdown = dashboardExpenseVatBreakdown(expense);
      const net = assetsReconRound(breakdown.costAmount);
      const accountCode = String(
        expense.fixedAsset?.assetAccountCode || expense.costAccountCode || expense.accountCode || '',
      );
      const capitalized = Boolean(expense.fixedAsset) && expense.fixedAsset.status !== 'deleted';
      const onAssetAccount = assetAccountSet.has(accountCode);
      const flags = [];
      // Over grensen og kostnadsført: spørsmålet må stilles.
      if (!capitalized && threshold !== null && net >= threshold) flags.push('mulig_aktivering');
      // På anleggskonto uten eiendelspost: balansen vokser uten at registeret
      // gjør det, og da kan ikke avskrivningen beregnes neste år.
      if (!capitalized && onAssetAccount) flags.push('direkteforing');
      if (capitalized && !hasPrimaryAttachment(expense)) flags.push('mangler_dok');
      return {
        id: expense.id,
        supplier: expense.supplierName || expense.description || t('rca.fallback.purchase', 'Kjøp'),
        voucher: expense.supplierInvoiceNumber || expense.voucherNumber || '',
        date: String(expense.date || '').slice(0, 10),
        accountCode,
        accountName: expense.costAccountName || '',
        description: expense.description || expense.category || '',
        net,
        vat: assetsReconRound(breakdown.deductibleVat),
        treatment: capitalized ? 'aktivert' : 'kostnadsfort',
        assetId: capitalized ? expense.id : null,
        flags,
      };
    })
    .filter((row) => row.flags.length || row.treatment === 'aktivert' || (threshold !== null && row.net >= threshold))
    .sort((left, right) => right.net - left.net);
};

// ------------------------------------------------------------------
// Avskrivninger
// ------------------------------------------------------------------
// Registeret regner PER ÅR og etter saldometoden — det er den metoden appens
// eiendelsside og årsoppgjør bruker. Bokføringen skjer i årsoppgjøret, ikke
// månedlig. Derfor sammenliknes årets forventede avskrivning med det som er
// bokført hittil i året, per saldogruppe: den bokførte avskrivningen ligger på
// gruppens kostnadskonto og kan ikke fordeles per eiendel uten å finne på en
// fordeling registeret ikke har.
//
// En gruppe der INGENTING er bokført er ikke et avvik i mars. Avskrivningen
// bokføres i årsoppgjøret, og differansen har da alltid samme årsak — den er
// navngitt som en egen linje i hovedavstemmingen i stedet. Et funn er det først
// når noe ER bokført og beløpet avviker, eller når året er omme uten at
// avskrivningen ble bokført. Uten det skillet ville hver eneste måned reist et
// varsel per saldogruppe som ingen kan lukke før i desember.
const assetsReconDepreciation = ({ assets, lines, range }) => {
  const yearOver = range.monthIndex === 11;
  const groups = safeArray(typeof FIXED_ASSET_GROUP_CONFIGS !== 'undefined' ? FIXED_ASSET_GROUP_CONFIGS : []);
  const bookedByAccount = new Map();
  for (const line of safeArray(lines)) {
    bookedByAccount.set(line.accountCode, assetsReconRound((bookedByAccount.get(line.accountCode) || 0) + num(line.amount)));
  }
  const rows = groups.map((group) => {
    const groupAssets = assets.filter((asset) => asset.group === group.id);
    const expected = assetsReconRound(groupAssets.reduce((sum, asset) => sum + asset.yearDepreciation, 0));
    const booked = bookedByAccount.has(group.depreciationAccountCode)
      ? assetsReconRound(bookedByAccount.get(group.depreciationAccountCode))
      : 0;
    const difference = assetsReconRound(expected - booked);
    return {
      group: group.id,
      label: t('asset.taxGroup.' + group.id, group.label),
      rate: group.rate,
      account: String(group.depreciationAccountCode),
      assets: groupAssets.length,
      expected,
      booked,
      difference,
      status: !groupAssets.length && !booked ? 'ikke_relevant'
        : Math.abs(difference) <= 0.5 ? 'stemmer'
          : booked > 0.005 || yearOver ? 'differanse'
            : 'ikke_bokfort_enda',
    };
  }).filter((row) => row.assets || row.booked);
  const expected = assetsReconRound(rows.reduce((sum, row) => sum + row.expected, 0));
  const booked = assetsReconRound(rows.reduce((sum, row) => sum + row.booked, 0));
  return {
    year: range.year,
    rows,
    expected,
    booked,
    difference: assetsReconRound(expected - booked),
    deviatingGroups: rows.filter((row) => row.status === 'differanse').length,
    notBookedGroups: rows.filter((row) => row.status === 'ikke_bokfort_enda').length,
    // Eiendeler i drift med bokført verdi som registeret ikke har en
    // avskrivningslinje for i året. Da er avskrivningsgrunnlaget ufullstendig.
    withoutPlan: assets.filter((asset) => asset.flags.includes('uten_avskrivning')).length,
  };
};

// ------------------------------------------------------------------
// Salg og utrangering
// ------------------------------------------------------------------
// Under saldometoden regnes det ikke en gevinst eller et tap per eiendel:
// salgsvederlaget reduserer saldogruppens avskrivningsgrunnlag. Skjermen
// beregner derfor ingen gevinst — den viser vederlaget, bokført verdi før
// avgangen og hva som er dokumentert, og lar vurderingen ligge hos den som
// avstemmer. En «beregnet gevinst» her ville vært et tall registeret ikke har.
const assetsReconDisposals = ({ assets, range, lines, assetAccounts, disposalLedger }) => {
  // Er ingenting bokfoert paa salgssumskontoene i perioden, er vederlaget paa
  // denne raden helt sikkert ikke bokfoert. Er noe bokfoert, kan ikke belopet
  // knyttes til den enkelte eiendelen — flere avganger deler konto — og da
  // sammenliknes totalene i stedet for aa gjette.
  const noProceedsBooked = Math.abs(num(disposalLedger?.proceeds)) < 0.005;
  const registered = assets
    .filter((asset) => asset.soldDate && asset.soldDate >= range.from && asset.soldDate <= range.to)
    .map((asset) => {
      const bookValue = asset.bookValueBeforeDisposal;
      // Regnskapsmessig gevinst eller tap: vederlaget minus bokfoert verdi.
      // Det er en REGNSKAPSMESSIG stoerrelse. Skattemessig reduserer vederlaget
      // saldogruppens avskrivningsgrunnlag i stedet, og de to skal ikke blandes.
      const gain = bookValue === null ? null : assetsReconRound(asset.saleAmount - bookValue);
      const flags = [];
      if (gain !== null && Math.abs(gain) > 0.5) flags.push('gevinst_til_kontroll');
      if (asset.saleAmount > 0.005 && noProceedsBooked) flags.push('vederlag_ikke_bokfort');
      if (!asset.documented) flags.push('mangler_dok');
      return {
        id: asset.id,
        assetId: asset.id,
        name: asset.name,
        date: asset.soldDate,
        saleAmount: asset.saleAmount,
        bookValue,
        gain,
        accountCode: asset.accountCode,
        documented: asset.documented,
        imported: asset.imported,
        kind: 'registrert',
        flags,
      };
    });
  // Kreditføringer på en anleggskonto som ingen registrert avgang svarer for.
  // Det er ikke en påstand om at noe er solgt — det er en postering som tar
  // verdi ut av balansen uten at registeret vet om det.
  const assetAccountSet = new Set(assetAccounts);
  const knownExpenseIds = new Set(assets.map((asset) => asset.expenseId));
  const unregistered = safeArray(lines)
    .filter((line) => assetAccountSet.has(line.accountCode))
    .filter((line) => num(line.amount) < -0.005)
    .filter((line) => line.sourceType !== 'yearEndEntry')
    .filter((line) => !knownExpenseIds.has(line.sourceId))
    .map((line) => ({
      id: 'linje:' + line.id,
      assetId: null,
      name: line.description || line.voucherName || t('rca.fallback.creditOnAssetAccount', 'Kreditføring på anleggskonto'),
      date: line.date,
      saleAmount: assetsReconRound(-num(line.amount)),
      bookValue: null,
      gain: null,
      accountCode: line.accountCode,
      documented: false,
      imported: false,
      kind: 'uregistrert',
      voucherNumber: line.voucherNumber || '',
      flags: ['mulig_avgang'],
    }));
  return [...registered, ...unregistered].sort((left, right) => String(left.date).localeCompare(String(right.date)));
};

// Kontoene en avgang treffer i resultatet. Salgssummen paa 388x og
// balanseverdien paa motkontoen; nettoen av dem ER den regnskapsmessige
// gevinsten eller tapet. Kontonumrene er den norske kontoplanens — som
// saldogruppene er de NO-spesifikke, og for SE/DK finnes ingen tilsvarende
// oppstilling i appen ennaa.
const ASSETS_RECON_PROCEEDS_ACCOUNTS = ['3881', '3882', '3887'];

const assetsReconDisposalLedger = ({ state, range }) => {
  const offset = window.FIXED_ASSET_DISPOSAL_OFFSET_ACCOUNT || '3889';
  const codes = [...ASSETS_RECON_PROCEEDS_ACCOUNTS, offset];
  const lines = typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes: codes, state })
    : [];
  // Salgssum staar med kredit i resultatet; motkontoen med debet. Begge snus til
  // positive tall her, slik at «vederlag» og «balanseverdi» leses som belop og
  // ikke som fortegn.
  const sum = (wanted) => assetsReconRound(lines
    .filter((line) => wanted.includes(line.accountCode))
    .reduce((total, line) => total + num(line.amount), 0));
  const proceeds = assetsReconRound(-sum(ASSETS_RECON_PROCEEDS_ACCOUNTS));
  const offsetAmount = sum([offset]);
  return {
    offsetAccount: offset,
    proceedsAccounts: ASSETS_RECON_PROCEEDS_ACCOUNTS,
    proceeds,
    offsetAmount,
    // Bokfoert netto paa avgangskontoene. Det er gevinsten slik BOKFOERINGEN
    // viser den — og den kan avvike fra registerets beregnede gevinst saa lenge
    // avskrivningen ikke er bokfoert, fordi motkontoen da baerer hele kostprisen.
    net: assetsReconRound(proceeds - offsetAmount),
    lines,
  };
};

// ------------------------------------------------------------------
// Direkteføringer på anleggskontoene
// ------------------------------------------------------------------
// En postering på en anleggskonto som verken kommer fra et registrert kjøp
// eller fra årsoppgjørets avskrivning. Kilden er bilagets egen (`sourceType`,
// `sourceId`), ikke teksten på linjen: en nøkkelordsregel på fritekst ville
// vært et varsel uten grunnlag.
const assetsReconDirectPostings = ({ lines, assets, assetAccounts }) => {
  const assetAccountSet = new Set(assetAccounts);
  const knownExpenseIds = new Set(assets.map((asset) => asset.expenseId));
  return safeArray(lines)
    .filter((line) => assetAccountSet.has(line.accountCode))
    .filter((line) => num(line.amount) > 0.005)
    .filter((line) => line.sourceType !== 'yearEndEntry')
    .filter((line) => !knownExpenseIds.has(line.sourceId))
    .map((line) => ({
      id: line.id,
      date: line.date,
      accountCode: line.accountCode,
      amount: assetsReconRound(line.amount),
      description: line.description || line.voucherName || '',
      voucherNumber: line.voucherNumber || '',
    }));
};

Object.assign(window, {
  assetsReconRound, assetsReconMonthRange, assetsReconThreshold, assetsReconGroupAccounts,
  assetsReconRegister, assetsReconAcquisitions, assetsReconDepreciation,
  assetsReconDisposals, assetsReconDisposalLedger, assetsReconDirectPostings,
  ASSETS_RECON_PROCEEDS_ACCOUNTS,
  ASSETS_RECON_SIGNIFICANCE,
});
